import { getBookNameByChapterId, parseChapterId } from "./bookNames";
import { resolvePreferredVerse } from "./readerVerseFocus";

export const formatBookName = (name) => {
  const raw = String(name || "").trim();
  if (!raw) return "";
  return raw.includes("-") ? raw.split("-").slice(1).join("-") : raw;
};

export const extractVerseMap = (content) => {
  const map = {};
  String(content || "")
    .split(/\r?\n/)
    .forEach((line) => {
      const match = line.trim().match(/^(\d+)\s*(.*)$/);
      if (match) {
        map[Number(match[1])] = (match[2] || "").trim();
      }
    });
  return map;
};

const QUOTE_PAIRS = [
  ["“", "”"],
  ["\"", "\""],
  ["「", "」"],
  ["『", "』"],
  ["‘", "’"],
  ["'", "'"],
];

export const stripUnmatchedLeadingQuotes = (raw) => {
  let text = String(raw || "");
  let changed = true;
  while (changed && text) {
    changed = false;
    for (const [open, close] of QUOTE_PAIRS) {
      if (!text.startsWith(open)) continue;
      const rest = text.slice(open.length);
      const closeCount = close ? rest.split(close).length - 1 : 0;
      const extraOpenCount = rest.split(open).length - 1;
      if (closeCount <= extraOpenCount) {
        text = rest;
        changed = true;
        break;
      }
    }
  }
  return text;
};

export const stripUnmatchedTrailingQuotes = (raw) => {
  let text = String(raw || "");
  let changed = true;
  while (changed && text) {
    changed = false;
    for (const [open, close] of QUOTE_PAIRS) {
      if (!close || open === close || !text.endsWith(close)) continue;
      const rest = text.slice(0, -close.length);
      const openCount = rest.split(open).length - 1;
      const extraCloseCount = rest.split(close).length - 1;
      if (openCount <= extraCloseCount) {
        text = rest;
        changed = true;
        break;
      }
    }
  }
  return text;
};

export const cleanVerseQuoteText = (raw) => {
  // Keep CUV ideographic spaces (e.g. before 神); only drop regular whitespace.
  return stripUnmatchedTrailingQuotes(stripUnmatchedLeadingQuotes(raw))
    .replace(/^[ \t\r\n\f\v\u00a0]+/, "")
    .replace(/[ \t\r\n\f\v\u00a0]+$/, "");
};

export const joinVerseTexts = (texts) =>
  (Array.isArray(texts) ? texts : [texts])
    .map((text) => String(text || ""))
    .join("");

export const buildNoteQuoteText = (texts) =>
  cleanVerseQuoteText(joinVerseTexts(texts));

export const quotePreviewLead = (formatted) =>
  String(formatted || "")
    .split("\n")
    .filter((line) => line.trim())[0] || "";

export const buildSelectionFormatText = ({
  format,
  verses = [],
  reference = "",
  shareUrl = "",
  quoteDash = "——",
} = {}) => {
  if (!verses.length) return "";
  const texts = verses.map((verse) => verse?.text);
  if (format === "plain") {
    return buildNoteQuoteText(texts);
  }
  if (format === "quote") {
    const body = buildNoteQuoteText(texts);
    return `${body}\n\n${quoteDash} ${reference}\n${shareUrl}`;
  }
  const body = verses
    .map((verse) => `${verse.number} ${cleanVerseQuoteText(verse.text)}`)
    .join("\n");
  return `${body}\n\n${reference}\n${shareUrl}`;
};

export const formatVerseReference = ({ bookName, chapterNumber, verse } = {}) => {
  const book = formatBookName(bookName) || "圣经";
  const ch = Number(chapterNumber) || 1;
  const v = Number(verse) || 1;
  return `${book} ${ch}:${v}`;
};

export const formatVerseQuote = ({ bookName, chapterNumber, verse, verseText }) => {
  const ref = formatVerseReference({ bookName, chapterNumber, verse });
  const text = cleanVerseQuoteText(verseText);
  return text ? `${ref} ${text}` : ref;
};

export const verseQuoteReference = (quote) => {
  const raw = String(quote || "").trim();
  const match = raw.match(/^(.+?\s+\d+:\d+)/);
  return match ? match[1] : raw;
};

export const firstVerseFromRange = (verseRange) => {
  const raw = String(verseRange || "").trim();
  if (!raw) return "";
  const match = raw.match(/(\d+)/);
  return match ? match[1] : "";
};

export const maskEmail = (email) => {
  const value = String(email || "").trim();
  const at = value.indexOf("@");
  if (at < 1) return value;
  const name = value.slice(0, at);
  const domain = value.slice(at + 1);
  if (name.length <= 2) return `${name.slice(0, 1)}***@${domain}`;
  return `${name.slice(0, 2)}***@${domain}`;
};

export const chapterPathWithVerse = (chapterId, verseRange, editionPath) => {
  const path = editionPath ? editionPath(chapterId) : `/chapter/${chapterId}`;
  const verse = firstVerseFromRange(verseRange);
  return verse ? `${path}?v=${encodeURIComponent(verse)}` : path;
};

export const parseChapterRoute = (pathname = "", search = "") => {
  const match = String(pathname || "").match(/\/(?:en\/|compare\/)?chapter\/([^/?#]+)/);
  const chapterId = match ? decodeURIComponent(match[1]) : "";
  const query = String(search || "");
  const params = new URLSearchParams(query.startsWith("?") ? query.slice(1) : query);
  const parsed = parseInt(params.get("v") || "", 10);
  return {
    chapterId,
    verse: Number.isFinite(parsed) && parsed > 0 ? parsed : null,
  };
};

const emptyMeta = () => ({ bookName: "", chapterNumber: null, editionId: "" });

const metadataFromMatch = (source, chapterId) => {
  if (!source || source.chapterId !== chapterId) {
    return emptyMeta();
  }
  return {
    bookName: source.bookName || "",
    chapterNumber: source.chapterNumber || null,
    editionId: source.editionId || "",
  };
};

const metadataFromChapterId = (chapterId) => {
  const parsed = parseChapterId(chapterId);
  if (!parsed.bookIndex) return emptyMeta();
  return {
    bookName: getBookNameByChapterId(chapterId, false) || "",
    chapterNumber: parsed.chapterNumber,
    editionId: "",
  };
};

const metadataForChapter = (chapterId, currentReader, lastRead) => {
  const fromSession = metadataFromMatch(currentReader, chapterId);
  if (fromSession.chapterNumber != null) return fromSession;
  const fromLast = metadataFromMatch(lastRead, chapterId);
  if (fromLast.chapterNumber != null) return fromLast;
  return metadataFromChapterId(chapterId);
};

const verseForChapter = (chapterId, readerVerse, routeVerse = null) => {
  const focus = readerVerse?.chapterId === chapterId ? readerVerse : null;
  return resolvePreferredVerse({
    selectedVerse: focus?.selectedVerse,
    currentVerse: focus?.currentVerse,
    routeVerse: routeVerse || focus?.routeVerse,
  });
};

const resolveFromChapter = (chapterId, readerVerse, currentReader, lastRead, routeVerse = null) => {
  const meta = metadataForChapter(chapterId, currentReader, lastRead);
  return {
    chapterId,
    verse: verseForChapter(chapterId, readerVerse, routeVerse),
    bookName: meta.bookName,
    chapterNumber: meta.chapterNumber,
    editionId: meta.editionId,
  };
};

export const resolveQuoteChapter = ({
  pathname = "",
  search = "",
  lastRead = null,
  currentReader = null,
  readerVerse = null,
} = {}) => {
  const fromRoute = parseChapterRoute(pathname, search);
  if (fromRoute.chapterId) {
    return resolveFromChapter(
      fromRoute.chapterId,
      readerVerse,
      currentReader,
      lastRead,
      fromRoute.verse
    );
  }
  // Quote the chapter the user is on or last read. A leftover selected verse
  // from an earlier quote/message must not override that reading position;
  // verse numbers from readerVerse are used only when they match this chapter.
  const session = currentReader?.chapterId ? currentReader : null;
  const fallback = session || lastRead;
  if (fallback?.chapterId) {
    return resolveFromChapter(fallback.chapterId, readerVerse, currentReader, lastRead);
  }
  if (readerVerse?.chapterId) {
    return resolveFromChapter(readerVerse.chapterId, readerVerse, currentReader, lastRead);
  }
  return resolveFromChapter("", readerVerse, currentReader, lastRead);
};

const stripUnmatchedQuoteAfterRef = (value) => {
  const quoteChars = QUOTE_PAIRS.map(([open]) => open).join("");
  const pattern = new RegExp(`([\\d：:]\\s*)([${quoteChars}])`, "g");
  return value.replace(pattern, (match, prefix, open, offset, full) => {
    const pair = QUOTE_PAIRS.find(([start]) => start === open);
    const close = pair?.[1];
    const rest = full.slice(offset + match.length);
    if (close && rest.includes(close)) return match;
    return prefix;
  });
};

// Render-time cleanup for legacy quotes like "引用经文：1-创世记 第13章".
export const sanitizeQuoteDisplay = (text) => {
  const stripped = String(text || "").replace(/(\d+)-(?=[\u4e00-\u9fffA-Za-z])/g, "");
  return stripUnmatchedQuoteAfterRef(stripped);
};
